import { useTranslation } from 'react-i18next';

import { OmBoligenFormData } from '../types';
import { UserRole } from '../../../../../../types/user';
import { ByggType } from '../../../../../../components/registering/utils/registerEnums';
import { MAX_NUMBER_OF_YEARS_IN_THE_FUTURE } from '../../../../../../components/registering/utils/validation';
import { useUser } from '../../../../../../hooks/useUser';
import { decimalsCount } from '../../../../../../utils/utils';

export const useOmBoligenValidation = () => {
  const { t } = useTranslation();
  const { user } = useUser();


  const validateBruksareal = (value?: number) => {
    // BRA is required
    if (!value || Number(value) <= 0) {
      return t('register.errorMessage.field.totalBRA');
    }
    // maks en desimal
    if (decimalsCount(value) > 1) {
      return t('register.errorMessage.field.BRA_desimal_presisjon');
    }
    return true;
  };

  const validateOppvarmetBruksareal = (value?: number, formValues?: OmBoligenFormData) => {
    // Oppvarmet BRA is required
    if (!value || Number(value) <= 0) {
      return t('register.errorMessage.field.totalOppvarmetBRA');
    }
    // maks en desimal
    if (decimalsCount(value) > 1) {
      return t('register.errorMessage.field.BRA_desimal_presisjon');
    }

    const totalArea = formValues?.bygginfo?.area;

    // Oppvarmet BRA must be less than or equal to total BRA
    if (!!totalArea && Number(value) > Number(totalArea)) {
      return t('register.errorMessage.field.totalOppvarmetBRA_custom');
    }
  };

  const validateOppvarmetBruksarealForHeleBygningen = (value?: number, formValues?: OmBoligenFormData) => {
    if (!value || Number(value) <= 0) {
      return t('register.bygningsdetaljer.totalOppvarmetBraBygning.error');
    }
    // maks en desimal
    if (decimalsCount(value) > 1) {
      return t('register.errorMessage.field.BRA_desimal_presisjon');
    }

    const totalOppvarmetBra = formValues?.bygginfo?.heatedArea;

    if (!!totalOppvarmetBra && Number(value) < totalOppvarmetBra) {
      return t('register.bygningsdetaljer.totalOppvarmetBraBygning.error_custom');
    }
  };

  const validateEtasjer = (value?: number, formValues?: OmBoligenFormData) => {
    const i18nPrefix =
      formValues?.bygginfo?.byggtype === ByggType.Leilighet
        ? 'register.bygningsdetaljer.leilighet.antallEtasjerUnntattKjeller'
        : 'register.bygningsdetaljer.antallEtasjerUnntattKjeller';

    if (!value || value < 1) {
      return t(`${i18nPrefix}.error`);
    }
    // Allow maximum 10 floors
    if (value > 10) {
      return t(`${i18nPrefix}.error_invalid`);
    }
    return true;
  };

  const validateYear = (year: number | undefined) => {
    // Require a 4 digit value
    if (!year || isNaN(year) || 1000 > year) {
      return t('register.bygningsdetaljer.byggear.error');
    }

    const currentYear = new Date().getFullYear();
    const isProfessional = user?.rolle === UserRole.PROFESJONELL;

    const constructionYear = year;
    // Check if year is before building was built
    if (year < constructionYear) {
      return t('register.parameters.error.year_min');
    }

    // Non experts cant register buildings built in the future
    if (!isProfessional && year > currentYear) {
      return t('register.errorMessage.field.byggAr_max');
    }

    // Professional users are allowed to register buildings that have not been built
    const allowedConstructionYear = currentYear + MAX_NUMBER_OF_YEARS_IN_THE_FUTURE;

    if (isProfessional && year > allowedConstructionYear) {
      return t('register.errorMessage.field.byggAr_maxProfessional');
    }
  };

  return { validateBruksareal, validateOppvarmetBruksareal, validateOppvarmetBruksarealForHeleBygningen, validateEtasjer, validateYear };
};
